import { useEffect, useState } from 'react';
import HiringService, { Candidate, Checkpoint, onCandidatesWritten } from '../../api/services/hiring.service';

/**
 * The rows each pipeline view last loaded, for this tab's lifetime.
 *
 * Not a data layer and not trying to be one: no invalidation beyond "the
 * next load overwrites it", and every visit still refetches. What it buys
 * is the first paint — the table is on screen while that request is in
 * flight, instead of a spinner where the table was a second ago. The
 * measured request is ~90ms; the wait people complained about was the
 * render, and a blank screen in front of it.
 *
 * It is also what makes the candidate page know its neighbours.
 */
const cache = new Map<string, { rows: Candidate[] }>();

export const cached = (mode: string) => cache.get(mode) || { rows: [] as Candidate[] };
export const putCached = (mode: string, rows: Candidate[]) => {
  cache.set(mode, { rows });
  // The table's fresh load of the open pipeline is also the queue's.
  if (mode === 'all') putQueue(rows);
};

/** Columns, once per session. The process does not change between visits. */
let catalogCache: Checkpoint[] = [];
export const cachedCatalog = () => catalogCache;
export const putCachedCatalog = (catalog: Checkpoint[]) => { catalogCache = catalog; };

/**
 * Module state outlives a test file's renders, so one test's rows would
 * paint inside the next test's screen. Exported for that, and used
 * nowhere else — the app has no reason to forget what it just loaded.
 */
export function resetPipelineCache() {
  cache.clear();
  catalogCache = [];
  queue = null;
}

/**
 * The open pipeline, newest first.
 *
 * Ordered by candidate number descending, always — NOT by whatever sort
 * the table happened to be in. The number is the order people arrived, the
 * newest arrivals are the ones being worked, and an order that changed
 * depending on which column was last clicked on a different screen would
 * make "next" mean something different every visit.
 *
 * Rows come from whatever the list last loaded; landing straight on a
 * candidate URL — a link somebody was sent — has nothing cached, so it
 * fetches the list itself, without checkpoints: this needs names and
 * stages, not cells.
 */
const newestFirst = (rows: Candidate[]) => [...rows].sort((a, b) => {
  // A row with no number yet (an import mid-flight) sorts to the end
  // rather than to the top, where it would look like the newest arrival.
  const an = a.num ?? -Infinity;
  const bn = b.num ?? -Infinity;
  return bn - an;
});

/**
 * The sidebar queue's rows, kept live.
 *
 * It used to be fetched once and then never again, so a stage change, a
 * new candidate or a deletion did not show in the sidebar until a full
 * page refresh. Now every candidate write (HiringService tells us) refetches
 * it while somebody is looking, and every mount revalidates behind the
 * rows it already has.
 *
 * Its own slot rather than cache 'all': the table's copy carries every
 * checkpoint cell and this fetch does not, and overwriting it would blank
 * the grid's cells on the way back to it.
 */
let queue: Candidate[] | null = null;
let queueInFlight: Promise<void> | null = null;
let queueAgain = false;
let writeTimer: ReturnType<typeof setTimeout> | null = null;
const queueListeners = new Set<(rows: Candidate[]) => void>();

function putQueue(rows: Candidate[]) {
  queue = newestFirst(rows);
  queueListeners.forEach((fn) => fn(queue!));
}

function refreshQueue(): Promise<void> {
  // A write that lands mid-request may not be in its answer; ask again
  // once it is back rather than trusting it.
  if (queueInFlight) { queueAgain = true; return queueInFlight; }
  queueInFlight = HiringService.list({ outcome: 'open', pageSize: 300 })
    .then((result) => { putQueue(result.items); })
    .catch(() => { /* The page still works without neighbours. */ })
    .finally(() => {
      queueInFlight = null;
      if (queueAgain) { queueAgain = false; refreshQueue(); }
    });
  return queueInFlight;
}

onCandidatesWritten(() => {
  // Nobody looking: the next mount revalidates anyway.
  if (queueListeners.size === 0) return;
  // Coalesced: editing a row in the grid is several saves in a second.
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => { writeTimer = null; refreshQueue(); }, 250);
});

export function usePipelineOrder(): { rows: Candidate[]; loading: boolean } {
  const [rows, setRows] = useState<Candidate[]>(() => queue || newestFirst(cached('all').rows));
  const [loading, setLoading] = useState(rows.length === 0);

  useEffect(() => {
    let live = true;
    const listener = (next: Candidate[]) => { setRows(next); setLoading(false); };
    queueListeners.add(listener);
    refreshQueue().finally(() => { if (live) setLoading(false); });
    return () => { live = false; queueListeners.delete(listener); };
  }, []);

  return { rows, loading };
}
